import CommonImg from "@/components/Common/Img";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/libs/utils";
import { Check } from "lucide-react";
import { KeyboardEvent, useCallback, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import DeleteIcon from "@/assets/images/delete-white.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type Option = Record<"value" | "label", string> & Record<string, string>;

type ControlledAutoCompleteProps = {
  label: string;
  name: string;
  placeholder: string;
  className?: string;
  commands?: Option[];
  inputValue?: string;
  setInputValue?: (value: string) => void;
};

type AutoCompleteProps = {
  options: Option[];
  value?: Option[];
  onValueChange?: (value: Option) => void;
  onValueRemove?: (value: Option) => void;
  isLoading?: boolean;
  placeholder?: string;
  inputValue?: string;
  setInputValue?: (value: string) => void;
};

export default function ControlledAutoComplete({
  label,
  name,
  placeholder,
  commands = [],
  inputValue,
  setInputValue,
}: ControlledAutoCompleteProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <>
              <AutoComplete
                options={commands}
                placeholder={placeholder}
                value={field.value}
                onValueChange={(value) => {
                  const values = field.value || [];
                  field.onChange([...values, value]);
                }}
                onValueRemove={(value: Option) => {
                  const values = field.value || [];
                  field.onChange(
                    values.filter((val: Option) => val.value !== value.value)
                  );
                }}
                isLoading={false}
                inputValue={inputValue}
                setInputValue={setInputValue}
              />
            </>
          </FormControl>
        </FormItem>
      )}
    />
  );
}

const AutoComplete = ({
  options,
  placeholder,
  value,
  onValueChange,
  onValueRemove,
  isLoading = false,
  inputValue = "",
  setInputValue = () => {},
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setOpen] = useState(false);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      if (!isOpen) {
        setOpen(true);
      }

      if (event.key === "Enter" && input.value !== "") {
        const optionToSelect = options.find(
          (option) => option.label === input.value
        );
        if (optionToSelect) {
          onValueChange?.(optionToSelect);
          setInputValue("");
        }
      }

      if (event.key === "Escape") {
        input.blur();
      }
    },
    [isOpen, options, onValueChange]
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
    setInputValue("");
  }, []);

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      const isAlreadySelected = value?.some(
        (val) => val.value === selectedOption.value
      );
      if (isAlreadySelected) {
        onValueRemove?.(selectedOption);
      } else {
        onValueChange?.(selectedOption);
      }
      // 멀티셀렉을 위해 드롭다운을 열린 상태로 유지
      // blur하지 않고 포커스 유지
    },
    [onValueChange, onValueRemove, value]
  );

  return (
    <Command onKeyDown={handleKeyDown} className="">
      <div className="border border-gray-C rounded-md">
        <CommandInput
          ref={inputRef}
          value={inputValue}
          onValueChange={isLoading ? undefined : setInputValue}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="text-sm"
        />
      </div>
      <div className="relative">
        <div
          className={cn(
            "animate-in fade-in-0 zoom-in-95 absolute top-0 z-10 w-full rounded-xl bg-white outline-none",
            isOpen ? "block" : "hidden"
          )}
        >
          <CommandList className="rounded-lg ring-1 ring-slate-200">
            {isLoading ? (
              <div className="p-1">
                <Skeleton className="h-8 w-full" />
              </div>
            ) : null}
            {options.length > 0 && !isLoading ? (
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = value?.some(
                    (val) => val.value === option.value
                  );
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className="flex w-full items-center gap-2"
                    >
                      <div className={cn(
                        "flex h-4 w-4 items-center justify-center rounded border",
                        isSelected
                          ? "bg-blue-ThermoFisher border-blue-ThermoFisher"
                          : "border-gray-300 bg-white"
                      )}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <WorkerItem worker={option} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {!isLoading ? (
              <CommandEmpty>검색 결과가 없습니다</CommandEmpty>
            ) : null}
          </CommandList>
        </div>

        {value && value.length > 0 && (
          <div className="flex flex-col gap-6 pt-6">
            {value.map((val) => (
              <div
                key={val.value}
                className="flex items-center justify-between"
              >
                <WorkerItem
                  worker={
                    options.find((option) => option.value === val.value) ?? val
                  }
                />

                <button
                  onClick={() => {
                    onValueRemove?.(val);
                  }}
                  className="h-7 flex items-center gap-1 rounded-sm bg-pink py-[6px] px-3"
                >
                  <CommonImg
                    src={DeleteIcon}
                    alt={"delete"}
                    className="text-white size-[16px]"
                  />
                  <span className="text-sm font-semibold text-white">빼기</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Command>
  );
};

const WorkerItem = ({ worker }: { worker: Option }) => {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold">
      <Avatar className="size-[24px]">
        <AvatarImage src={worker.image_url || worker.img_url} />
        <AvatarFallback>P</AvatarFallback>
      </Avatar>
      <span className="text-gray-A">{worker.label}</span>
      <span className="text-dark-red">{worker.position}</span>
      <span className="text-gray-B">{worker.email}</span>
    </div>
  );
};
